import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fmt$0, fmtPct, gmColor, gmBg } from '../lib/pricing';
import { staticTierToMILabel, tierLabel } from '../lib/marketRates';
import { dealUrlFor } from '../lib/hubspot';
// v3.5.67: per-user quote-type permissions
import { useAuth } from '../contexts/AuthContext';
import { getAllowedQuoteTypes } from '../lib/quotePermissions';
import { v3, colors } from '../lib/styleTokens';
import { Pill, Icon } from '../components/v3';

// v3.9.8: rep-driven quote types (Voice / Bundle / FlexIT) don't create a
// `proposals` row — their Service Order / agreement lives in
// quotes.inputs.signwellDocuments. Surface its status in the Proposal column so
// the list shows "SO Sent / Viewed / Signed" instead of a blank dash. LOA and
// International-Dialing-Waiver docs also live in that array — exclude them.
const AGREEMENT_DOC_TYPES = new Set(['voice_quote', 'bundle_quote', 'flexit_quote', 'service_order']);
function latestAgreementDoc(q) {
  const docs = q?.inputs?.signwellDocuments || [];
  const agr = docs.filter(d => d && AGREEMENT_DOC_TYPES.has(d.type) && String(d.status || '').toLowerCase() !== 'cancelled');
  if (!agr.length) return null;
  return agr.reduce((a, b) =>
    new Date(b.created_at || 0) > new Date(a.created_at || 0) ? b : a);
}

export default function QuotesPage() {
  const [quotes,  setQuotes]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [showNewMenu, setShowNewMenu] = useState(false);
  // v3.5.81: proposal status per quote, keyed by quote_id. We render a
  // small pill in a dedicated 'Proposal' column so reps can see at a
  // glance which quotes have proposals out — and which of those have
  // been viewed or approved. Loaded in parallel with the quotes table
  // (one extra round-trip; trivial cost) and merged client-side.
  const [proposalsByQuote, setProposalsByQuote] = useState({});
  const navigate = useNavigate();
  // v3.5.67: filter the New Quote dropdown by the user's allowed quote types.
  // If they have zero allowed types, the button is rendered disabled with a
  // tooltip — they're a read-only-of-existing-quotes account.
  const { profile } = useAuth();
  const allowedTypes = getAllowedQuoteTypes(profile);
  const hasNoCreatePerms = allowedTypes.length === 0;

  async function load() {
    setLoading(true);
    let q = supabase.from('quotes').select('*').order('created_at', { ascending: false });
    if (status) q = q.eq('status', status);
    if (search) q = q.ilike('client_name', `%${search}%`);
    const { data } = await q.limit(100);
    const quotes = data || [];
    setQuotes(quotes);

    // v3.5.81: fetch all non-superseded proposals for these quotes in one
    // round-trip. Map by quote_id, keeping the most recent (sent_at desc).
    if (quotes.length > 0) {
      const quoteIds = quotes.map(qq => qq.id);
      const { data: props } = await supabase.from('proposals')
        .select('id,quote_id,status,sent_at,last_viewed_at,view_count,approved_at,public_token')
        .in('quote_id', quoteIds)
        .neq('status', 'superseded')
        .order('sent_at', { ascending: false });
      const byQuote = {};
      (props || []).forEach(p => {
        // First entry per quote_id wins because sent_at desc is the order.
        if (!byQuote[p.quote_id]) byQuote[p.quote_id] = p;
      });
      setProposalsByQuote(byQuote);
    } else {
      setProposalsByQuote({});
    }

    setLoading(false);
  }
  useEffect(() => { load(); }, [search, status]);

  return (
    <div style={{ padding: v3.layout.pagePadding, background: v3.color.bg, fontFamily: v3.font.family, minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: v3.font.size.pageTitle, fontWeight: v3.font.weight.semibold, color: v3.color.body, margin: 0, letterSpacing: v3.font.letterSpacing.tight }}>Saved Quotes</h2>
          <p style={{ fontSize: v3.font.size.label, color: v3.color.muted, marginTop: 4 }}>All team quotes — searchable and shareable</p>
        </div>
        <div style={{ position:'relative' }}>
          {/* v3.5.67: button is disabled when user has zero allowed quote
              types. Tooltip explains why so they don't think it's broken. */}
          <button
            onClick={() => !hasNoCreatePerms && setShowNewMenu(v => !v)}
            disabled={hasNoCreatePerms}
            title={hasNoCreatePerms ? 'You don\'t have permission to create new quotes. Ask an admin to enable quote types in Admin → Users.' : ''}
            style={{
              padding:'9px 16px',
              background: hasNoCreatePerms ? v3.color.mutedSoft : v3.color.ink,
              color:'white', border:'none', borderRadius:v3.radius.input, fontSize:v3.font.size.label, fontWeight:v3.font.weight.semibold,
              cursor: hasNoCreatePerms ? 'not-allowed' : 'pointer',
              opacity: hasNoCreatePerms ? 0.6 : 1,
            }}>
            + New Quote ▾
          </button>
          {showNewMenu && !hasNoCreatePerms && (
            <>
              <div onClick={() => setShowNewMenu(false)} style={{ position:'fixed', inset:0, zIndex:99 }}/>
              <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', background:v3.color.card, border:`1px solid ${v3.color.border}`, borderRadius:v3.radius.card, boxShadow:v3.shadow.modal, zIndex:100, minWidth:210, overflow:'hidden' }}>
                {/* v3.5.67: items filtered by allowedTypes from
                    getAllowedQuoteTypes(profile). The visual icon/badge/color
                    map keys off QUOTE_TYPES.key so it stays in sync with the
                    canonical type list. Removing or adding a quote type is a
                    one-line change in src/lib/quotePermissions.js. */}
                {allowedTypes.map(({ key, label, path }) => {
                  const meta = ({
                    managed_it: { icon: 'monitor',  text: 'Managed IT',         badge: 'IT',     color: '#2563eb' },
                    voice:      { icon: 'phone',    text: 'Hosted Voice',       badge: 'Voice',  color: '#7c3aed' },
                    bundle:     { icon: 'package',  text: 'Bundle (IT + Voice)', badge: 'Bundle', color: '#6d28d9' },
                    multisite:  { icon: 'building', text: 'Multi-Location IT',  badge: 'Multi',  color: '#0f766e' },
                    flexit:     { icon: 'zap',      text: 'FlexIT On-Demand',    badge: 'FlexIT', color: '#f97316' },
                  })[key] || { icon: null, text: label, badge: key, color: v3.color.muted };
                  return (
                    <div key={path} onClick={() => { navigate(path); setShowNewMenu(false); }}
                      style={{ padding:'10px 14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, borderBottom:`1px solid ${v3.color.borderSoft}` }}
                      onMouseEnter={e => e.currentTarget.style.background = v3.color.cardAlt}
                      onMouseLeave={e => e.currentTarget.style.background = v3.color.card}>
                      <span style={{ fontSize:v3.font.size.label, color:v3.color.bodySoft, display:'flex', alignItems:'center', gap:8 }}>
                        {meta.icon && <span style={{ color: meta.color, display:'flex' }}><Icon name={meta.icon} /></span>} {meta.text}
                      </span>
                      <span style={{ fontSize:v3.font.size.micro, fontWeight:v3.font.weight.bold, padding:'1px 6px', borderRadius:v3.radius.pill, background: meta.color + '18', color: meta.color }}>{meta.badge}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by client name..."
          style={{ flex: 1, padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, outline: 'none', color: v3.color.body }} />
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, background: v3.color.card, color: v3.color.body }}>
          <option value="">All statuses</option>
          {['draft','in_review','approved','sent','won','lost','expired'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? <div style={{ padding: 20, color: v3.color.muted, fontSize: v3.font.size.body }}>Loading...</div> : (
        <div style={{ background: v3.color.card, borderRadius: v3.radius.cardLarge, border: `1px solid ${v3.color.border}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: v3.font.size.body }}>
            <thead>
              <tr style={{ background: v3.color.cardAlt }}>
                {/* v3.5.62: 'Onboarding' renamed to 'One-Time Charges' — column
                    actually shows all non-recurring revenue (implementation,
                    voice hardware/porting NRC, FlexIT prepay or Flex Block
                    setup), not just onboarding fees. Field name (totals.
                    onboarding) is unchanged so existing data displays without
                    migration; only the label moves.
                    v3.5.81.21: shortened to 'NRC' on this page only — reps
                    know what NRC means and the saved-quotes grid is cramped
                    for column real estate. Other surfaces (Deal Summary,
                    proposal totals) still read the longer label for
                    client-facing clarity. */}
                {['Quote #','Client','Package','Market','MRR','GM','NRC','Status','Proposal','Created','HubSpot'].map(h => (
                  <th key={h} style={{ padding: v3.space.cellPad, textAlign: 'left', color: v3.color.muted, fontWeight: v3.font.weight.semibold, borderBottom: `1px solid ${v3.color.border}`, fontSize: v3.font.size.micro, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase, whiteSpace: 'nowrap', minWidth: h === 'NRC' ? 130 : undefined }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: v3.color.mutedSoft, fontSize: v3.font.size.body }}>No quotes found. Create your first quote above.</td></tr>
              )}
              {quotes.map((q, i) => {
                const totals = q.totals || {};
                const gm = totals.impliedGM || totals.gm || 0;
                // v3.5.70: drop vestigial q.quote_type === 'flexIT' check.
                // The quotes table has no quote_type column; package_name is
                // the single source of truth. Matches v3.5.70 cleanup of
                // quoteRowToType in src/lib/quotePermissions.js.
                // v3.5.91.1: type detection moved from package_name prefix-
                // matching to the quote_type column.
                // v3.5.91.2: defensive fallback removed — package_name no
                // longer carries the type prefix; the CHECK constraint on
                // quote_type guarantees one of five canonical keys, so no
                // fallback is needed. Booleans are mutually exclusive by
                // definition (qt is exactly one of five values).
                const qt = q.quote_type || 'managed_it';
                const isFlexIT    = qt === 'flexit';
                const isVoice     = qt === 'voice';
                const isBundle    = qt === 'bundle';
                const isMultiSite = qt === 'multisite';
                return (
                  <tr key={q.id} onClick={() => navigate(isFlexIT ? `/flexIT/${q.id}` : isMultiSite ? `/multisite/${q.id}` : isBundle ? `/bundle/${q.id}` : isVoice ? `/voice/${q.id}` : `/quotes/${q.id}`)} style={{ borderBottom: `1px solid ${v3.color.borderSoft}`, background: i % 2 === 0 ? v3.color.card : v3.color.cardAlt, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = v3.color.inkBg}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? v3.color.card : v3.color.cardAlt}>
                    <td style={{ padding: v3.space.cellPad, fontFamily: v3.font.mono, fontSize: v3.font.size.small, color: v3.color.ink, fontWeight: v3.font.weight.semibold }}>{q.quote_number}</td>
                    <td style={{ padding: v3.space.cellPad, fontWeight: v3.font.weight.semibold, color: v3.color.body }}>
                      {q.client_name}
                      {/* v3.7.x: type badges flattened to solid tokens (no gradient — binding §4). */}
                      {isFlexIT && <span style={{ marginLeft:6, fontSize:v3.font.size.micro, background:colors.quoteType.flexit, color:'white', padding:'1px 6px', borderRadius:v3.radius.pill, fontWeight:v3.font.weight.bold }}>FlexIT</span>}
                      {isMultiSite && <span style={{ marginLeft:6, fontSize:v3.font.size.micro, background:colors.quoteType.multisite, color:'white', padding:'1px 6px', borderRadius:v3.radius.pill, fontWeight:v3.font.weight.bold }}>Multi-Site</span>}
                      {isBundle && !isMultiSite && <span style={{ marginLeft:6, fontSize:v3.font.size.micro, background:colors.quoteType.bundle, color:'white', padding:'1px 6px', borderRadius:v3.radius.pill, fontWeight:v3.font.weight.bold }}>Bundle</span>}
                      {isVoice && !isBundle && <span style={{ marginLeft:6, fontSize:v3.font.size.micro, background:colors.quoteType.voice, color:'white', padding:'1px 6px', borderRadius:v3.radius.pill, fontWeight:v3.font.weight.bold }}>Voice</span>}
                    </td>
                    <td style={{ padding: v3.space.cellPad, color: v3.color.bodySoft }}>{q.package_name}</td>
                    {/* v3.5.64: use existing tier helpers from marketRates so
                        Market column displays consistent labels across all
                        quote types and ages. staticTierToMILabel maps legacy
                        ZIP-prefix keys (major_metro/mid_market/small_market/
                        rural) to their modern MI vocabulary equivalents
                        (Premium/Standard/Adjusted/Secondary); tierLabel
                        formats the modern keys with proper capitalization.
                        Old quotes with legacy tier values now display modern
                        labels without any database backfill. */}
                    <td style={{ padding: v3.space.cellPad, color: v3.color.muted }}>{q.market_tier ? tierLabel(staticTierToMILabel(q.market_tier)) : '—'}</td>
                    <td style={{ padding: v3.space.cellPad, fontFamily: v3.font.mono, fontWeight: v3.font.weight.semibold, color: v3.color.body }}>{fmt$0(totals.finalMRR)}</td>
                    <td style={{ padding: v3.space.cellPad }}>
                      <span style={{ padding: '2px 8px', borderRadius: v3.radius.pill, fontSize: v3.font.size.micro, fontWeight: v3.font.weight.bold, background: gmBg(gm), color: gmColor(gm), fontFamily: v3.font.mono }}>
                        {fmtPct(gm)}
                      </span>
                    </td>
                    {/* v3.5.86.4: NRC column shows the EFFECTIVE
                        onboarding amount when an incentive is applied
                        (q.onboarding_incentive.effectiveFee), falling back
                        to totals.onboarding (raw fee) when no incentive is
                        active. The persisted totals.onboarding is
                        intentionally the raw fee (so contract value math
                        still works); the effective amount lives in
                        onboarding_incentive.effectiveFee and is computed
                        at display time here.

                        v3.5.86.9 (Phase 0 #5): visual indicator added —
                        when an incentive is active, the cell shifts to
                        amber italic with a subtle ↓ prefix and a title=
                        tooltip showing the original fee + discount %. Reps
                        scanning the saved-quotes list can now see at a
                        glance which quotes have promos applied and what
                        the original fee was, without opening each one.
                        Color + italic + arrow = three independent visual
                        signals (accessibility — color alone fails for
                        color-blind reps).

                        v3.5.92.3: hidden bug fix. Pre-deploy logic
                        compared inc.effectiveFee (post-discount LABOR
                        only) against totals.onboarding (which on Voice
                        and Bundle pre-v3.5.92.2 was TOTAL NRC including
                        hardware). A Voice quote with $200 Programming +
                        $1000 phones + 50%-off incentive displayed as
                        "$100 — Was $1200 — 92% off applied" — utterly
                        wrong: 92% calculated against the wrong base, $100
                        understating the actual upfront charge by $1000.
                        Post-deploy reads totals.nrc (canonical total NRC
                        since v3.5.92.2) for the customer-facing one-time
                        figure, totals.onboarding (canonical labor base
                        since v3.5.92.2) for the discount-against-labor
                        math, and adds the non-labor subcategory fields
                        to the effective total. Display = effective labor +
                        all non-labor NRC. Tooltip clarifies the discount
                        applies to Programming labor specifically.

                        Old saves (pre-v3.5.92.2) where totals.nrc is
                        undefined fall back to totals.onboarding for both
                        "labor base" and "total NRC" — same behavior as
                        pre-deploy. The bug only manifested on pre-92.2
                        Voice/Bundle saves with active incentives;
                        post-92.2 saves render correctly and the residual
                        bug surface shrinks every time an old quote is
                        re-saved. */}
                    {(() => {
                      const inc = q.onboarding_incentive;
                      // Labor base (subject to incentive). Post-v3.5.92.2:
                      // totals.onboarding === Programming & Configuration
                      // labor only across all five types. Pre-v3.5.92.2
                      // it varied: Voice/Bundle = total NRC, IT/MultiSite/
                      // FlexIT = labor (no hardware on those). Either way
                      // it's what the picker was discounting against, so
                      // pct-off math works under both vintages.
                      const labor = Number(totals.onboarding) || 0;
                      // Non-labor NRC (post-v3.5.92.2 fields; absent on old
                      // saves → coerces to 0 which is correct for old
                      // IT/MultiSite/FlexIT and matches the legacy buggy
                      // semantic for old Voice/Bundle).
                      const nonLaborNRC = (Number(totals.hardwareNRC) || 0)
                                        + (Number(totals.activationNRC) || 0)
                                        + (Number(totals.portingNRC) || 0);
                      // Total raw NRC pre-incentive. Prefer totals.nrc
                      // (canonical since v3.5.92.2); fall back to
                      // totals.onboarding for old saves where the field
                      // sometimes meant "total NRC".
                      const rawTotal = Number(totals.nrc) || labor + nonLaborNRC || labor;
                      const effLabor = inc?.effectiveFee != null ? Number(inc.effectiveFee) : labor;
                      const effTotal = effLabor + nonLaborNRC;
                      // v3.9.7: show the canonical NET one-time — post onboarding
                      // incentive AND post one-time discounts (totals.netNRC, the
                      // same figure on the quote screen, Stripe prepay, and Service
                      // Order). Fall back to the incentive-only effTotal for old
                      // saves that predate netNRC persistence.
                      const display = totals.netNRC != null ? Number(totals.netNRC) : effTotal;
                      const active = display < rawTotal - 0.005;  // reduced by incentive and/or one-time discount
                      const discountAmt = Math.max(0, rawTotal - display);
                      return (
                        <td
                          style={{
                            padding: v3.space.cellPad,
                            fontFamily: v3.font.mono,
                            color: active ? v3.color.status.amberText : v3.color.bodySoft,
                            fontStyle: active ? 'italic' : 'normal',
                            cursor: active ? 'help' : 'default',
                            whiteSpace: 'nowrap',
                            minWidth: 130,
                          }}
                          title={active ? `Was ${fmt$0(rawTotal)} — reduced ${fmt$0(discountAmt)} (onboarding incentive and/or one-time discounts)` : ''}
                        >
                          {active && '↓ '}{fmt$0(display)}
                        </td>
                      );
                    })()}
                    <td style={{ padding: v3.space.cellPad }}>
                      <Pill variant={q.status}>{q.status}</Pill>
                    </td>
                    {/* v3.5.81: Proposal pill — none / sent / viewed / approved / declined / expired.
                        State-aware color matches the proposal banner palette on QuotePage.
                        v3.5.99.0.1: amber override on `approved` when the Service Order hasn't
                        been sent yet — derived from quotes.service_order_sent_at column.
                        Amber = "deal won, rep needs to send the SO"; green = "SO sent, done."
                        Color matches the existing viewed/expired amber palette for consistency. */}
                    <td style={{ padding: v3.space.cellPad }}>
                      {(() => {
                        const p = proposalsByQuote[q.id];
                        if (!p) {
                          // No proposal row — for rep-driven types show the Service
                          // Order / agreement status straight from signwellDocuments.
                          const so = latestAgreementDoc(q);
                          if (!so) return <span style={{ fontSize: v3.font.size.small, color: v3.color.faint }}>—</span>;
                          const soMap = {
                            sent:      { bg:'#dbeafe', color:'#1e40af', label:'SO Sent' },
                            viewed:    { bg:'#fef3c7', color:'#92400e', label:'SO Viewed' },
                            signed:    { bg:'#dcfce7', color:'#166534', label:'SO Signed' },
                            completed: { bg:'#dcfce7', color:'#166534', label:'SO Signed' },
                            declined:  { bg:'#fee2e2', color:'#991b1b', label:'SO Declined' },
                            expired:   { bg:'#fef3c7', color:'#92400e', label:'SO Expired' },
                          };
                          const sm = soMap[String(so.status || '').toLowerCase()] || { bg:'#dbeafe', color:'#1e40af', label:'SO Sent' };
                          const when = so.created_at ? new Date(so.created_at).toLocaleDateString() : '';
                          return <span title={`Service Order ${so.status}${when ? ` · sent ${when}` : ''}`} style={{ padding:'2px 8px', borderRadius:v3.radius.pill, fontSize:v3.font.size.micro, fontWeight:v3.font.weight.bold, background:sm.bg, color:sm.color }}>{sm.label}</span>;
                        }
                        const map = {
                          sent_for_review: { bg:'#dbeafe', color:'#1e40af', label:'Sent' },
                          viewed:          { bg:'#fef3c7', color:'#92400e', label:`Viewed${p.view_count > 1 ? ` ${p.view_count}×` : ''}` },
                          approved:        { bg:'#dcfce7', color:'#166534', label:'Approved' },
                          declined:        { bg:'#fee2e2', color:'#991b1b', label:'Declined' },
                          expired:         { bg:'#fef3c7', color:'#92400e', label:'Expired' },
                          draft:           { bg:'#f3f4f6', color:'#6b7280', label:'Draft' },
                        };
                        let m = map[p.status] || map.draft;
                        // v3.5.99.0.1: if approved but no SO sent yet, render amber instead of green.
                        // Mutates a copy of `m` so the static map stays intact for other rows.
                        if (p.status === 'approved' && !q.service_order_sent_at) {
                          m = { bg:'#fef3c7', color:'#92400e', label:'Approved – SO Pending' };
                        }
                        return <span style={{ padding:'2px 8px', borderRadius:v3.radius.pill, fontSize:v3.font.size.micro, fontWeight:v3.font.weight.bold, background:m.bg, color:m.color }}>{m.label}</span>;
                      })()}
                    </td>
                    <td style={{ padding: v3.space.cellPad, color: v3.color.muted, fontSize: v3.font.size.small }}>{new Date(q.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: v3.space.cellPad }}>
                      {/*
                        Render the View Deal link whenever the quote has a
                        hubspot_deal_id, even if hubspot_deal_url wasn't
                        captured at connect time (which happened to some
                        older quotes — the proxy occasionally returned a deal
                        without a dealUrl field, and the front-end persisted
                        an empty string). The URL is derived from the deal id
                        as a fallback.
                      */}
                      {(q.hubspot_deal_url || q.hubspot_deal_id)
                        ? <a href={q.hubspot_deal_url || dealUrlFor(q.hubspot_deal_id)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                            style={{ fontSize: v3.font.size.small, color: '#f97316', fontWeight: v3.font.weight.semibold }}>View Deal →</a>
                        : <span style={{ fontSize: v3.font.size.small, color: v3.color.faint }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
